import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../../../../ui/components/Button';
import { Input } from '../../../../ui';
import { faLocationDot, faCheck, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '../../../../ui/utils/cn';

/** Matches one pasted line like `40.18428112931128, 22.000251462436264` (spaces around comma ok). */
const LAT_LNG_PAIR = /^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/;

function parseLatLngPair(text: string): { latStr: string; lngStr: string } | null {
    const m = text.trim().match(LAT_LNG_PAIR);
    if (!m) return null;
    return { latStr: m[1], lngStr: m[2] };
}

interface LocationControlsProps {
    isHomeMade: boolean;
    hasGpsLocation: boolean;
    isGettingLocation: boolean;
    locationError: string | null;
    locationText: string;
    onGetLocation: () => void;
    coordinates?: {
        lat: number;
        lng: number;
    };
    isAdmin?: boolean;
    onManualOverride?: (lat: number, lng: number) => void;
    onStoreCoordinatesCommit?: (lat: number, lng: number) => void;
}

const LocationControls: React.FC<LocationControlsProps> = ({
    isHomeMade,
    hasGpsLocation,
    isGettingLocation,
    locationError,
    locationText,
    onGetLocation,
    coordinates,
    isAdmin = false,
    onManualOverride,
    onStoreCoordinatesCommit,
}) => {
    const latInputRef = useRef<HTMLInputElement>(null);
    const lngInputRef = useRef<HTMLInputElement>(null);
    const latId = React.useId();
    const lngId = React.useId();
    const [overrideText, setOverrideText] = useState('');
    const [overrideError, setOverrideError] = useState<string | null>(null);
    const [coordLatStr, setCoordLatStr] = useState('');
    const [coordLngStr, setCoordLngStr] = useState('');
    const [coordCommitError, setCoordCommitError] = useState<string | null>(null);

    // Everyone can adjust coords after a GPS fix, or type them manually when GPS failed (admins also have the paste override below).
    const showCoordInputs =
        !isHomeMade && (hasGpsLocation || !!locationError);

    useEffect(() => {
        if (!showCoordInputs || coordinates == null) return;
        if (
            latInputRef.current === document.activeElement ||
            lngInputRef.current === document.activeElement
        ) {
            return;
        }
        const blankFieldsForManualEntry =
            coordinates.lat === 0 && coordinates.lng === 0 && locationError && !hasGpsLocation;
        if (blankFieldsForManualEntry) {
            setCoordLatStr('');
            setCoordLngStr('');
            return;
        }
        setCoordLatStr(String(coordinates.lat));
        setCoordLngStr(String(coordinates.lng));
    }, [
        coordinates?.lat,
        coordinates?.lng,
        showCoordInputs,
        locationError,
        hasGpsLocation,
    ]);

    const handleOverrideSubmit = () => {
        setOverrideError(null);
        const trimmed = overrideText.trim();
        const match = trimmed.match(LAT_LNG_PAIR);
        if (!match) {
            setOverrideError('expected format: lat,lng (e.g. 48.85, 2.35)');
            return;
        }
        const lat = parseFloat(match[1]);
        const lng = parseFloat(match[2]);
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            setOverrideError('lat must be -90..90 and lng -180..180');
            return;
        }
        onManualOverride?.(lat, lng);
    };

    const handleCoordCommit = () => {
        setCoordCommitError(null);
        if (!onStoreCoordinatesCommit) return;
        const fromLatField = parseLatLngPair(coordLatStr.trim());
        const fromLngField = parseLatLngPair(coordLngStr.trim());
        const pair = fromLatField || fromLngField;
        const lat = pair ? parseFloat(pair.latStr) : parseFloat(coordLatStr.trim());
        const lng = pair ? parseFloat(pair.lngStr) : parseFloat(coordLngStr.trim());
        if (Number.isNaN(lat) || Number.isNaN(lng)) {
            setCoordCommitError('enter valid latitude and longitude numbers');
            return;
        }
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            setCoordCommitError('lat must be -90..90 and lng -180..180');
            return;
        }
        onStoreCoordinatesCommit(lat, lng);
    };

    const handleCoordPairPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const parsed = parseLatLngPair(e.clipboardData.getData('text/plain'));
        if (!parsed) return;
        e.preventDefault();
        setCoordCommitError(null);
        setCoordLatStr(parsed.latStr);
        setCoordLngStr(parsed.lngStr);
    };

    if (isHomeMade) return null;

    const coordEditor =
        showCoordInputs && onStoreCoordinatesCommit ? (
            <div className="mt-2 space-y-2">
                <p className="text-xs text-muted-foreground">
                    tweak latitude/longitude if gps is off, then apply.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="min-w-[6.5rem] flex-1">
                        <label htmlFor={latId} className="sr-only">
                            Latitude
                        </label>
                        <Input
                            ref={latInputRef}
                            id={latId}
                            type="text"
                            inputMode="decimal"
                            aria-label="Latitude"
                            placeholder="Latitude or paste lat, lng"
                            value={coordLatStr}
                            onPaste={handleCoordPairPaste}
                            onChange={(e) => {
                                setCoordCommitError(null);
                                setCoordLatStr(e.target.value);
                            }}
                            className="h-9 text-xs font-mono"
                        />
                    </div>
                    <div className="min-w-[6.5rem] flex-1">
                        <label htmlFor={lngId} className="sr-only">
                            Longitude
                        </label>
                        <Input
                            ref={lngInputRef}
                            id={lngId}
                            type="text"
                            inputMode="decimal"
                            aria-label="Longitude"
                            placeholder="Longitude"
                            value={coordLngStr}
                            onPaste={handleCoordPairPaste}
                            onChange={(e) => {
                                setCoordCommitError(null);
                                setCoordLngStr(e.target.value);
                            }}
                            className="h-9 text-xs font-mono"
                        />
                    </div>
                    <Button
                        type="button"
                        onClick={handleCoordCommit}
                        variant="outline"
                        size="sm"
                        className="h-9 shrink-0 px-3"
                    >
                        apply coordinates
                    </Button>
                </div>
                {coordCommitError && (
                    <div className="text-xs text-pink">{coordCommitError}</div>
                )}
            </div>
        ) : null;

    return (
        <div className="space-y-3">
            {/* location button and status */}
            <div 
                className={cn(
                    "p-4 border border-ink/10 bg-muted/20 rounded-xl shadow-sm transition-all",
                    hasGpsLocation && "bg-mint/5 border-mint/30",
                    locationError && "bg-pink/5 border-pink/30"
                )}
            >
                <div className="flex items-start gap-4">
                    {/* status icon */}
                    <div 
                        className={cn(
                            "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full",
                            hasGpsLocation ? "bg-mint/20 text-mint" : "bg-sky/20 text-sky"
                        )}
                    >
                        <FontAwesomeIcon 
                            icon={hasGpsLocation ? faCheck : faLocationDot} 
                            className="h-4 w-4" 
                        />
                    </div>

                    {/* content */}
                    <div className="flex-1 space-y-2">
                        {!hasGpsLocation ? (
                            <>
                                <div className="text-sm font-medium">
                                    Location Required
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    We need your location to find nearby coffee shops (or enter coordinates below)
                                </div>
                                <Button
                                    type="button"
                                    onClick={onGetLocation}
                                    disabled={isGettingLocation}
                                    variant="sky"
                                    icon={faLocationDot}
                                    iconPosition="left"
                                    loading={isGettingLocation}
                                    size="sm"
                                    className="mt-2"
                                >
                                    {isGettingLocation ? 'Getting Location...' : 'Get My Location'}
                                </Button>
                                {coordEditor}
                            </>
                        ) : (
                            <>
                                {/* Row 1: place + refresh — avoids Update aligning to vertical center of tall left column */}
                                <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0 text-sm font-medium text-foreground">
                                        📍 {locationText}
                                    </div>
                                    <Button
                                        onClick={onGetLocation}
                                        disabled={isGettingLocation}
                                        variant="outline"
                                        icon={faRefresh}
                                        iconPosition="left"
                                        loading={isGettingLocation}
                                        size="sm"
                                        type="button"
                                        className="shrink-0"
                                    >
                                        Update
                                    </Button>
                                </div>
                                {coordEditor}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* error display */}
            {locationError && (
                <div className="flex items-start gap-2 p-3 bg-pink/5 border border-pink/30 rounded-lg text-sm">
                    <span className="text-pink">⚠️</span>
                    <div>
                        <div className="font-medium text-pink">We need your location to add a coffee</div>
                        <div className="text-xs text-muted-foreground mt-1">{locationError}</div>
                    </div>
                </div>
            )}

            {/* admin manual override */}
            {isAdmin && onManualOverride && (locationError || hasGpsLocation) && (
                <div className="p-3 border border-sky/30 bg-sky/5 rounded-lg space-y-2">
                    <div className="text-xs font-medium text-sky">
                        🛠️ Admin override — paste lat,lng
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Input
                            type="text"
                            placeholder="48.85, 2.35"
                            value={overrideText}
                            onChange={(e) => setOverrideText(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleOverrideSubmit();
                                }
                            }}
                            className="h-9 min-w-[10rem] flex-1 text-sm font-mono"
                        />
                        <Button
                            type="button"
                            onClick={handleOverrideSubmit}
                            disabled={!overrideText.trim()}
                            variant="sky"
                            size="sm"
                            className="h-9 shrink-0"
                        >
                            Apply
                        </Button>
                    </div>
                    {overrideError && (
                        <div className="text-xs text-pink">{overrideError}</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default LocationControls;
